import type { IPolicy } from '@adornis/abac/core/types.js';
import { isNullish } from '@adornis/assert/isNullish.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import {
  ensureLoggedInPayload,
  type LoggedInEvaluationFunction,
} from '../permissions/abac-payloads/checkLoggedInPayload.js';
import { MARTINUS_ABAC_PAYLOAD } from '../permissions/abac-payloads/MartinusAbacPayload.js';
import { usersHaveSameSchoolOnRoles } from '../permissions/api/getSchoolIDsForRoles.js';
import { hasRoles } from '../permissions/api/hasRoles.js';
import { isSelf } from '../permissions/api/isSelf.js';
import { EMPLOYEE_ROLES, Role } from '../permissions/roles.js';
import type { UserOnUserPayload } from '../users/UserPolicy.js';

export enum ContractsActions {
  /**
   * Can see the contracts of a person
   */
  VIEW_CONTRACTS = 'VIEW_CONTRACTS',

  /**
   * Can change the contract data of a person
   */
  EDIT_CONTRACT = 'EDIT_CONTRACT',

  /**
   * Can change a supplement itself, i.e. its runtimes or the changes it carries
   */
  EDIT_SUPPLEMENT = 'EDIT_SUPPLEMENT',

  /**
   * Can record the signature of a supplement. Separate from editing because signing is what puts the
   * supplement legally into effect.
   */
  SIGN_SUPPLEMENT = 'SIGN_SUPPLEMENT',

  /**
   * Can delete a whole contract including its meta information
   */
  DELETE_CONTRACT = 'DELETE_CONTRACT',
}

/**
 * A PSB must not work on the data of a Schulleiter of their own school, since that is the person they report
 * to. Mirrors `checkIfPSBCanEditingSchulleiterData`.
 */
const psbMayEditPerson = (
  currentUser: { roles: Array<{ name: string; contexts: string[] }> },
  viewedUser: { roles: Array<{ name: string; contexts: string[] }> },
) => {
  const psb = currentUser.roles.find(role => role.name === Role.PSB);
  const schulleiter = viewedUser.roles.find(role => role.name === Role.SCHULLEITER);
  if (!psb || !schulleiter) return true;
  return !psb.contexts.some(schoolID => schulleiter.contexts.includes(schoolID));
};

const ContractsPolicyFunctions: Record<ContractsActions, LoggedInEvaluationFunction<UserOnUserPayload>> = {
  /**
   * The person themselves, the administrative roles and the MAV can view contracts. The MAV needs them for the
   * MAV process.
   */
  [ContractsActions.VIEW_CONTRACTS]: async request => {
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: [Role.SCHULLEITER, Role.PSB, Role.MAV],
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Verträge sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer hat keine Berechtigung für den Vertrags-Tab.' };
  },

  /**
   * Editing the contract data needs a writing role on the school of the person. Editing the own contract is
   * reserved for admins, as nobody should change their own employment data.
   */
  [ContractsActions.EDIT_CONTRACT]: async request => {
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: false, reason: 'Nutzer darf seinen eigenen Vertrag nicht bearbeiten.' };
    }

    if (!request.payload.viewedUser) {
      return { allowed: false, reason: 'Es ist keine Person angegeben, deren Vertrag bearbeitet werden soll.' };
    }

    if (!psbMayEditPerson(request.payload.currentUser, request.payload.viewedUser)) {
      return {
        allowed: false,
        reason: 'Die Personalsachbearbeitung darf die Daten der Schulleitung der eigenen Schule nicht bearbeiten.',
      };
    }

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: [Role.SCHULLEITER, Role.PSB],
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Vertrag bearbeiten. Da er eine schreibende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Working on the supplement itself is a task of the Personalsachbearbeitung, which is why this is bound to
   * the global role instead of a single school.
   */
  [ContractsActions.EDIT_SUPPLEMENT]: async request => {
    const isGlobalPSB = hasRoles({
      user: request.payload.currentUser,
      roles: [Role.PSB],
      contexts: [GLOBAL_CONTEXT],
    });

    if (!isGlobalPSB) {
      return { allowed: false, reason: 'Nur die Personalsachbearbeitung darf Nachträge bearbeiten.' };
    }

    if (request.payload.viewedUser && !psbMayEditPerson(request.payload.currentUser, request.payload.viewedUser)) {
      return {
        allowed: false,
        reason: 'Die Personalsachbearbeitung darf die Daten der Schulleitung der eigenen Schule nicht bearbeiten.',
      };
    }

    return { allowed: true, reason: 'Nutzer ist Personalsachbearbeitung und darf Nachträge bearbeiten.' };
  },

  /**
   * Recording the signature follows the same rule as editing a supplement.
   */
  [ContractsActions.SIGN_SUPPLEMENT]: async request => {
    return ContractsPolicyFunctions[ContractsActions.EDIT_SUPPLEMENT](request);
  },

  /**
   * Deleting a contract cannot be undone and is therefore reserved for the Personalsachbearbeitung.
   */
  [ContractsActions.DELETE_CONTRACT]: async request => {
    const isGlobalPSB = hasRoles({
      user: request.payload.currentUser,
      roles: [Role.PSB],
      contexts: [GLOBAL_CONTEXT],
    });

    if (isGlobalPSB) {
      return { allowed: true, reason: 'Nutzer ist Personalsachbearbeitung und darf Verträge löschen.' };
    }

    return { allowed: false, reason: 'Nur die Personalsachbearbeitung darf Verträge löschen.' };
  },
};

export const ContractsPolicy: IPolicy<UserOnUserPayload> = {
  name: 'ContractsPolicy',
  acceptingPayloadTypes: [MARTINUS_ABAC_PAYLOAD],
  async evaluate(request) {
    /**
     * Check logged in user
     */
    request.payload = ensureLoggedInPayload(request.payload);

    /**
     * Execute the correct function for the action
     */
    const result = await ContractsPolicyFunctions[request.payload.action]?.(request);

    if (isNullish(result)) return false;
    return result;
  },
};
